import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ListenService, ListConfig, FeinbodenformEntry, BodenartEntry, ListMetadata } from './listen.service';
import { Router } from '@angular/router';
import { DataService } from '../data.service';
import { AuthService } from '../auth.service';
import { OfflineModeService, ConfirmService } from '@luzula/luzula-lib';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
  selector: 'app-listenverwaltung',
  templateUrl: './listenverwaltung.component.html',
  styleUrls: ['./listenverwaltung.component.css'],
  
})
export class ListenverwaltungComponent implements OnInit, OnDestroy {
  private listenService = inject(ListenService);
  private router = inject(Router);
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private oms = inject(OfflineModeService);
  private confirmService = inject(ConfirmService);
  private storageSub?: Subscription;

  availableLists: ListConfig[] = [];
  selectedListName = '';
  selectedConfig?: ListConfig;

  listData: any[] = [];
  filteredData: any[] = [];
  searchTerm = '';

  hasChanges = false;
  isCustomList = false;
  storageType: 'database' | 'local' | 'none' = 'none';
  listMetadata: ListMetadata | null = null;

  // Bearbeitungsmodus für simple/valueNamePair
  editingIndex = -1;
  editValue = '';
  editName = '';

  // Neuer Eintrag für simple/valueNamePair
  showNewEntry = false;
  newValue = '';
  newName = '';

  // Feinbodenform Modal
  showFbfModal = false;
  fbfEditMode: 'new' | 'edit' = 'new';
  fbfEditIndex = -1;
  fbfEdit: FeinbodenformEntry = this.emptyFbfEntry();

  // Bodenart Modal
  showBodenartModal = false;
  bodenartEditMode: 'new' | 'edit' = 'new';
  bodenartEditIndex = -1;
  bodenartEdit: BodenartEntry = this.emptyBodenartEntry();

  // Zuordnungen bearbeiten
  zuordnungKeys: string[] = [];
  newZuordnungKey = '';
  newZuordnungValue = '';

  // Berechtigungsprüfung: nur "datennutzer" im Online-Modus
  get canEdit(): boolean {
    const isOnline = !this.oms.isOffline();
    const isDatennutzer = this.authService.amI('datennutzer');
    return isOnline && isDatennutzer;
  }

  get disabledReason(): string {
    if (this.oms.isOffline()) {
      return 'Bearbeitung nur im Online-Modus möglich';
    }
    if (!this.authService.amI('datennutzer')) {
      return 'Bearbeitung nur für Datennutzer';
    }
    return '';
  }

  ngOnInit() {
    this.availableLists = this.listenService.editableLists;
    if (this.availableLists.length > 0) {
      this.selectList(this.availableLists[0].name);
    }

    // Auf Verbindungsänderungen reagieren und storageType aktualisieren
    this.storageSub = this.listenService.watchStorageType().subscribe(type => {
      this.storageType = type;
    });
  }

  ngOnDestroy() {
    this.storageSub?.unsubscribe();
  }

  private emptyBodenartEntry(): BodenartEntry {
    return {
      name: '',
      Lagerungsdichte: { gering: 0, mittel: 0, hoch: 0 },
      Humusgehalt: { h2: 0, h3: 0, h4: 0, h5: 0, h6: 0 }
    };
  }

  private emptyFbfEntry(): FeinbodenformEntry {
    return {
      FBF: '',
      FBF_display: '',
      name: '',
      HBF: '',
      HBF_lang: '',
      NKr: '',
      Hw: null,
      zuordnung: {},
      Serie: null,
      Seite: ''
    };
  }

  async selectList(listName: string) {
    if (this.hasChanges) {
      const confirmed = await this.confirmService.confirmDiscard('Es gibt ungespeicherte Änderungen. Wirklich wechseln?');
      if (!confirmed) {
        return;
      }
    }

    this.selectedListName = listName;
    this.selectedConfig = this.listenService.getListConfig(listName);
    this.listData = await this.listenService.getList(listName) || [];
    this.isCustomList = await this.listenService.hasCustomList(listName);
    this.storageType = this.listenService.getStorageType();
    this.listMetadata = await this.listenService.getListMetadata(listName);
    this.hasChanges = false;
    this.cancelEdit();
    this.cancelNewEntry();
    this.closeFbfModal();
    this.closeBodenartModal();
    this.applyFilter();
  }

  applyFilter() {
    if (!this.searchTerm) {
      this.filteredData = [...this.listData];
    } else {
      const term = this.searchTerm.toLowerCase();
      if (this.selectedConfig?.type === 'simple') {
        this.filteredData = this.listData.filter(item =>
          item.toLowerCase().includes(term)
        );
      } else if (this.selectedConfig?.type === 'bodenartenTabelle') {
        this.filteredData = this.listData.filter((item: BodenartEntry) =>
          item.name?.toLowerCase().includes(term)
        );
      } else if (this.selectedConfig?.type === 'feinbodenform') {
        this.filteredData = this.listData.filter((item: FeinbodenformEntry) =>
          item.FBF?.toLowerCase().includes(term) ||
          item.FBF_display?.toLowerCase().includes(term) ||
          item.name?.toLowerCase().includes(term) ||
          item.HBF?.toLowerCase().includes(term) ||
          item.HBF_lang?.toLowerCase().includes(term) ||
          item.NKr?.toLowerCase().includes(term) ||
          item.Seite?.toLowerCase().includes(term)
        );
      } else {
        this.filteredData = this.listData.filter(item =>
          item.value?.toLowerCase().includes(term) ||
          item.name?.toLowerCase().includes(term)
        );
      }
    }
  }

  // Bearbeitung für simple/valueNamePair
  startEdit(index: number) {
    if (this.selectedConfig?.type === 'feinbodenform') {
      this.startFbfEdit(index);
      return;
    }
    if (this.selectedConfig?.type === 'bodenartenTabelle') {
      this.startBodenartEdit(index);
      return;
    }
    const realIndex = this.getRealIndex(index);
    this.editingIndex = realIndex;
    const item = this.listData[realIndex];
    if (this.selectedConfig?.type === 'simple') {
      this.editValue = item;
    } else {
      this.editValue = item.value;
      this.editName = item.name;
    }
  }

  saveEdit() {
    if (this.editingIndex < 0) return;

    if (this.selectedConfig?.type === 'simple') {
      if (!this.editValue.trim()) {
        alert('Wert darf nicht leer sein');
        return;
      }
      this.listData[this.editingIndex] = this.editValue.trim();
    } else {
      if (!this.editValue.trim()) {
        alert('Wert darf nicht leer sein');
        return;
      }
      this.listData[this.editingIndex] = {
        value: this.editValue.trim(),
        name: this.editName.trim()
      };
    }

    this.hasChanges = true;
    this.cancelEdit();
    this.applyFilter();
  }

  cancelEdit() {
    this.editingIndex = -1;
    this.editValue = '';
    this.editName = '';
  }

  // Neuer Eintrag für simple/valueNamePair
  startNewEntry() {
    if (this.selectedConfig?.type === 'feinbodenform') {
      this.startFbfNew();
      return;
    }
    if (this.selectedConfig?.type === 'bodenartenTabelle') {
      this.startBodenartNew();
      return;
    }
    this.showNewEntry = true;
    this.newValue = '';
    this.newName = '';
  }

  addNewEntry() {
    if (this.selectedConfig?.type === 'simple') {
      if (!this.newValue.trim()) {
        alert('Wert darf nicht leer sein');
        return;
      }
      this.listData.push(this.newValue.trim());
    } else {
      if (!this.newValue.trim()) {
        alert('Wert darf nicht leer sein');
        return;
      }
      this.listData.push({
        value: this.newValue.trim(),
        name: this.newName.trim()
      });
    }

    this.hasChanges = true;
    this.cancelNewEntry();
    this.sortList();
    this.applyFilter();
  }

  cancelNewEntry() {
    this.showNewEntry = false;
    this.newValue = '';
    this.newName = '';
  }

  // Löschen
  async deleteEntry(index: number) {
    const realIndex = this.getRealIndex(index);
    const item = this.listData[realIndex];
    let displayValue: string;
    if (this.selectedConfig?.type === 'simple') {
      displayValue = item;
    } else if (this.selectedConfig?.type === 'bodenartenTabelle') {
      displayValue = item.name;
    } else if (this.selectedConfig?.type === 'feinbodenform') {
      displayValue = item.FBF_display || item.FBF;
    } else {
      displayValue = item.value;
    }

    const confirmed = await this.confirmService.confirmDelete(`"${displayValue}" wirklich löschen?`);
    if (confirmed) {
      this.listData.splice(realIndex, 1);
      this.hasChanges = true;
      this.applyFilter();
    }
  }

  // Sortieren
  sortList() {
    if (this.selectedConfig?.type === 'simple') {
      this.listData.sort((a, b) => a.localeCompare(b, 'de'));
    } else if (this.selectedConfig?.type === 'bodenartenTabelle') {
      this.listData.sort((a: BodenartEntry, b: BodenartEntry) =>
        (a.name || '').localeCompare(b.name || '', 'de')
      );
    } else if (this.selectedConfig?.type === 'feinbodenform') {
      this.listData.sort((a: FeinbodenformEntry, b: FeinbodenformEntry) =>
        (a.FBF || '').localeCompare(b.FBF || '', 'de')
      );
    } else {
      this.listData.sort((a, b) => a.value.localeCompare(b.value, 'de'));
    }
  }

  // Speichern
  async saveList() {
    try {
      this.sortList();
      await this.listenService.saveList(this.selectedListName, this.listData);
      // DataService Cache aktualisieren
      await this.dataService.reloadCustomList(this.selectedListName);
      // Metadaten aktualisieren
      this.listMetadata = await this.listenService.getListMetadata(this.selectedListName);
      this.hasChanges = false;
      this.isCustomList = true;
      alert('Liste gespeichert');
    } catch (e: any) {
      console.error('Fehler beim Speichern:', e);
      alert('Fehler beim Speichern: ' + (e.message || e));
    }
  }

  // Verwerfen - lädt gespeicherten Stand neu
  async discardChanges() {
    const confirmed = await this.confirmService.confirmDiscard('Alle ungespeicherten Änderungen verwerfen?');
    if (!confirmed) {
      return;
    }

    this.listData = await this.listenService.getList(this.selectedListName) || [];
    this.hasChanges = false;
    this.cancelEdit();
    this.cancelNewEntry();
    this.closeFbfModal();
    this.closeBodenartModal();
    this.applyFilter();
  }

  // Formatiert ISO-Datum für Anzeige
  formatDate(isoDate: string): string {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Zurück zur Startseite
  async goBack() {
    if (this.hasChanges) {
      const confirmed = await this.confirmService.confirmDiscard('Es gibt ungespeicherte Änderungen. Wirklich verlassen?');
      if (!confirmed) {
        return;
      }
    }
    this.router.navigate(['/']);
  }

  // Hilfsfunktionen
  private getRealIndex(filteredIndex: number): number {
    const filteredItem = this.filteredData[filteredIndex];
    return this.listData.indexOf(filteredItem);
  }

  isEditing(index: number): boolean {
    return this.getRealIndex(index) === this.editingIndex;
  }

  // ========== Feinbodenform Modal ==========

  startFbfNew() {
    this.fbfEditMode = 'new';
    this.fbfEditIndex = -1;
    this.fbfEdit = this.emptyFbfEntry();
    this.zuordnungKeys = [];
    this.newZuordnungKey = '';
    this.newZuordnungValue = '';
    this.showFbfModal = true;
  }

  startFbfEdit(filteredIndex: number) {
    const realIndex = this.getRealIndex(filteredIndex);
    this.fbfEditMode = 'edit';
    this.fbfEditIndex = realIndex;
    const item = this.listData[realIndex] as FeinbodenformEntry;
    // Deep copy
    this.fbfEdit = {
      FBF: item.FBF || '',
      FBF_display: item.FBF_display || '',
      name: item.name || '',
      HBF: item.HBF || '',
      HBF_lang: item.HBF_lang || '',
      NKr: item.NKr || '',
      Hw: item.Hw,
      zuordnung: { ...item.zuordnung },
      Serie: item.Serie,
      Seite: item.Seite || ''
    };
    this.zuordnungKeys = Object.keys(this.fbfEdit.zuordnung);
    this.newZuordnungKey = '';
    this.newZuordnungValue = '';
    this.showFbfModal = true;
  }

  closeFbfModal() {
    this.showFbfModal = false;
    this.fbfEdit = this.emptyFbfEntry();
    this.zuordnungKeys = [];
  }

  saveFbfEntry() {
    if (!this.fbfEdit.FBF.trim()) {
      alert('FBF darf nicht leer sein');
      return;
    }

    // Leere Strings in null umwandeln
    const entry: FeinbodenformEntry = {
      ...this.fbfEdit,
      Hw: this.fbfEdit.Hw?.trim() || null,
      Serie: this.fbfEdit.Serie?.trim() || null
    };

    if (this.fbfEditMode === 'new') {
      this.listData.push(entry);
    } else {
      this.listData[this.fbfEditIndex] = entry;
    }

    this.hasChanges = true;
    this.closeFbfModal();
    this.sortList();
    this.applyFilter();
  }

  // Zuordnungen verwalten
  addZuordnung() {
    if (!this.newZuordnungKey.trim()) {
      alert('Schlüssel darf nicht leer sein');
      return;
    }
    this.fbfEdit.zuordnung[this.newZuordnungKey.trim()] = this.newZuordnungValue.trim();
    this.zuordnungKeys = Object.keys(this.fbfEdit.zuordnung);
    this.newZuordnungKey = '';
    this.newZuordnungValue = '';
  }

  removeZuordnung(key: string) {
    delete this.fbfEdit.zuordnung[key];
    this.zuordnungKeys = Object.keys(this.fbfEdit.zuordnung);
  }

  updateZuordnung(key: string, value: string) {
    this.fbfEdit.zuordnung[key] = value;
  }

  // ========== Bodenart Modal ==========

  startBodenartNew() {
    this.bodenartEditMode = 'new';
    this.bodenartEditIndex = -1;
    this.bodenartEdit = this.emptyBodenartEntry();
    this.showBodenartModal = true;
  }

  startBodenartEdit(filteredIndex: number) {
    const realIndex = this.getRealIndex(filteredIndex);
    this.bodenartEditMode = 'edit';
    this.bodenartEditIndex = realIndex;
    const item = this.listData[realIndex] as BodenartEntry;
    this.bodenartEdit = {
      name: item.name || '',
      Lagerungsdichte: { ...item.Lagerungsdichte },
      Humusgehalt: { ...item.Humusgehalt }
    };
    this.showBodenartModal = true;
  }

  closeBodenartModal() {
    this.showBodenartModal = false;
    this.bodenartEdit = this.emptyBodenartEntry();
  }

  saveBodenartEntry() {
    if (!this.bodenartEdit.name.trim()) {
      alert('Kürzel darf nicht leer sein');
      return;
    }

    const entry: BodenartEntry = {
      name: this.bodenartEdit.name.trim(),
      Lagerungsdichte: { ...this.bodenartEdit.Lagerungsdichte },
      Humusgehalt: { ...this.bodenartEdit.Humusgehalt }
    };

    if (this.bodenartEditMode === 'new') {
      this.listData.push(entry);
    } else {
      this.listData[this.bodenartEditIndex] = entry;
    }

    this.hasChanges = true;
    this.closeBodenartModal();
    this.sortList();
    this.applyFilter();
  }
}
